import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FiChevronDown, FiChevronUp, FiMenu, FiX } from "react-icons/fi";

const logo = "/img/logo.svg";

const services = [
  { label: "Website Development", href: "/service/website-design-development-solutions" },
  { label: "Custom Website Development", href: "/service/custom-website-design-development-solutions" },
  { label: "E-Commerce Website", href: "/service/ecommerce-website-design-development-solutions" },
  { label: "CMS Website", href: "/service/cms-website-design-development-solutions" },
  { label: "Mobile App Development", href: "/service/mobile-app-design-development-solutions" },
  { label: "Software Development", href: "/software-development-services" },
  { label: "Digital Marketing", href: "/services/digital-marketing" },
  { label: "Performance Marketing", href: "/services/performance-marketing" },
  { label: "Social Media Marketing", href: "/service/social-media-marketing" },
  { label: "SEO", href: "/service/search-engine-optimization-seo" },
];

const links = [
  { label: "Home", href: "/" },
  { label: "Services", dropdown: true },
  { label: "Technologies", href: "/#technologies" },
  { label: "Projects", href: "/our-completed-projects" },
  { label: "About us", href: "/about-us" },
  { label: "Contact us", href: "/contact-us" },
];

const Navbar = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesRef = useRef(null);

  // Close the Services dropdown on outside click or Escape.
  useEffect(() => {
    if (!servicesOpen) return undefined;
    const onDown = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setServicesOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setServicesOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [servicesOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const servicesActive = services.some((s) => s.href === pathname);
  const isActive = (l) => (l.dropdown ? servicesActive : l.href === pathname);

  const linkCls = (active) =>
    `relative py-2 text-[17px] font-medium tracking-[0.01em] transition hover:text-[#3D6EF7] xl:text-[clamp(15px,1.04vw,20px)] ${
      active
        ? "text-[#3D6EF7] after:absolute after:-bottom-[6px] after:left-0 after:h-[3px] after:w-full after:rounded-full after:bg-[#3D6EF7]"
        : "text-[#111111]"
    }`;

  return (
    <div className="tw">
      <header
        className={`fixed inset-x-0 top-0 z-[1030] transition-all ${
          scrolled ? "bg-[#F5F7FD]/95 shadow-[0_4px_20px_-10px_rgba(15,23,42,0.18)] backdrop-blur-md" : "bg-[#F5F7FD]"
        }`}
      >
        <nav className="mx-auto flex h-[76px] max-w-[1900px] items-center justify-between px-[16px] sm:px-[24px] xl:h-[clamp(76px,5.83vw,112px)] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
          <a href="/" aria-label="DataMetricks home" className="shrink-0">
            <img src={logo} alt="DataMetricks Consulting Private Limited" className="h-[40px] w-auto xl:h-[clamp(36px,2.6vw,50px)]" />
          </a>

          <div className="hidden items-center xl:flex xl:gap-[clamp(20px,2vw,38px)]">
          {/* Desktop links */}
          <ul className="flex items-center gap-[clamp(20px,2.6vw,50px)]">
            {links.map((l) =>
              l.dropdown ? (
                <li
                  key={l.label}
                  ref={servicesRef}
                  className="relative"
                >
                  <button
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={servicesOpen}
                    onClick={() => setServicesOpen((v) => !v)}
                    className={`flex items-center gap-[6px] border-0 bg-transparent ${linkCls(isActive(l))}`}
                  >
                    {l.label}
                    {servicesOpen ? <FiChevronUp strokeWidth={3} className="text-[0.9em]" /> : <FiChevronDown strokeWidth={3} className="text-[0.9em]" />}
                  </button>
                  <div
                    className={`absolute left-1/2 top-full w-[520px] -translate-x-1/2 pt-[16px] transition duration-200 ${
                      servicesOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-[0]"
                    }`}
                  >
                    <div className="grid grid-cols-2 gap-1 rounded-2xl bg-white p-[12px] shadow-card ring-1 ring-slate-100">
                      {services.map((s) => (
                        <a
                          key={s.href}
                          href={s.href}
                          onClick={() => setServicesOpen(false)}
                          className={`rounded-xl px-[14px] py-2.5 text-[14px] font-medium transition hover:bg-brand-soft hover:text-brand-blue ${
                            s.href === pathname ? "bg-brand-soft text-brand-blue" : "text-slate-700"
                          }`}
                        >
                          {s.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </li>
              ) : (
                <li key={l.label}>
                  <a href={l.href} className={linkCls(isActive(l))}>
                    {l.label}
                  </a>
                </li>
              )
            )}
          </ul>

          <a
            href="/contact-us"
            className="inline-flex h-[clamp(44px,2.92vw,56px)] items-center justify-center whitespace-nowrap rounded-full bg-gradient-to-r from-[#3D6EF7] to-[#7A4CF6] px-[clamp(24px,3.4vw,65px)] text-[clamp(15px,1.04vw,20px)] font-semibold tracking-[0.01em] text-white transition hover:brightness-110"
          >
            Get a Quote
          </a>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen(!open)}
            className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-xl border-0 bg-white text-[22px] text-ink shadow-card xl:hidden"
          >
            {open ? <FiX /> : <FiMenu />}
          </button>
        </nav>

        {/* Mobile panel */}
        <div
          className={`overflow-y-auto bg-white transition-all duration-300 xl:hidden ${
            open ? "max-h-[calc(100vh-76px)] border-t border-slate-100" : "max-h-0"
          }`}
        >
          <ul className="flex flex-col gap-1 px-[16px] py-[16px]">
            {links.map((l) =>
              l.dropdown ? (
                <li key={l.label}>
                  <button
                    type="button"
                    onClick={() => setMobileServices(!mobileServices)}
                    className={`flex w-full items-center justify-between rounded-xl border-0 bg-transparent px-[12px] py-[12px] text-left text-[16px] font-medium ${
                      isActive(l) ? "text-brand-blue" : "text-ink"
                    }`}
                  >
                    {l.label}
                    <FiChevronDown className={`transition ${mobileServices ? "rotate-180" : ""}`} />
                  </button>
                  {mobileServices && (
                    <div className="ml-[12px] flex flex-col border-l-2 border-brand-soft pl-[12px]">
                      {services.map((s) => (
                        <a key={s.href} href={s.href} className="py-2 text-[14px] text-slate-600 hover:text-brand-blue">
                          {s.label}
                        </a>
                      ))}
                    </div>
                  )}
                </li>
              ) : (
                <li key={l.label}>
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className={`block rounded-xl px-[12px] py-[12px] text-[16px] font-medium ${
                      isActive(l) ? "bg-brand-soft text-brand-blue" : "text-ink"
                    }`}
                  >
                    {l.label}
                  </a>
                </li>
              )
            )}
            <li className="pt-2">
              <a
                href="/contact-us"
                className="flex justify-center rounded-full bg-gradient-brand px-8 py-[14px] text-[16px] font-semibold text-white shadow-glow"
              >
                Get a Quote
              </a>
            </li>
          </ul>
        </div>
      </header>
    </div>
  );
};

export default Navbar;
